/**
 * Revenue Calculator Module
 * 
 * This module provides functionality for calculating and displaying
 * revenue impact based on customer data and churn reduction.
 */

const revenueCalculator = {
  /**
   * Initialize the revenue calculator in the specified container
   * @param {HTMLElement} container - The container element for the calculator
   * @returns {Promise} A promise that resolves when initialization is complete
   */
  initialize: function(container) {
    console.log('Initializing revenue calculator');
    
    // Create the HTML structure for the calculator
    container.innerHTML = this.getCalculatorHTML();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Ensure the initial message is visible and results are hidden
    const initialMessage = document.getElementById('resultsInitialMessage');
    const resultsContent = document.getElementById('resultsContent');
    
    if (initialMessage && resultsContent) {
      initialMessage.classList.remove('hidden');
      resultsContent.classList.add('hidden');
    }
    
    return Promise.resolve();
  },
  
  /**
   * Get the HTML markup for the calculator
   * @returns {string} HTML markup
   */
  getCalculatorHTML: function() {
    return `
      <div class="revenue-calculator mt-4">
        <!-- Title and subtitle section -->
        <div class="mb-8">
          <h1 class="text-6xl text-gray-900 mb-4">Revenue Impact Calculator</h1>
          <p class="text-lg text-gray-600">Add your details, we'll calculate your potential revenue gains</p>
        </div>
        
        <div class="flex flex-col md:flex-row gap-8 mb-4">
          <!-- Inputs Section - Left side -->
          <div class="w-full md:w-1/3 order-1 md:order-1">
            <form id="revenueForm" style="padding: 1.25rem;" class="bg-gray-50 rounded-lg">              
              <div style="margin-bottom: 28px;">
                <label for="customersPerYear" class="block text-sm font-medium text-gray-700 mb-2">How many new customers do you add annually?</label>
                <input type="number" id="customersPerYear" class="form-input block w-full border border-gray-300 rounded-md p-2" value="1000" min="0">
              </div>
              
              <div style="margin-bottom: 28px;">
                <label for="churnRate" class="block text-sm font-medium text-gray-700 mb-2">Enter your annual churn rate (%):</label>
                <input type="number" id="churnRate" class="form-input block w-full border border-gray-300 rounded-md p-2" value="50" min="0" max="100">
              </div>
              
              <div style="margin-bottom: 32px;">
                <label for="annualSales" class="block text-sm font-medium text-gray-700 mb-2">Enter your annual revenue per customer ($):</label>
                <input type="number" id="annualSales" class="form-input block w-full border border-gray-300 rounded-md p-2" value="500" min="0">
              </div>
              
              <div>
                <button type="button" id="calculateBtn" class="brand-button w-full">Calculate Impact</button>
              </div>
            </form>
          </div>
          
          <!-- Results Section - Right side -->
          <div class="w-full md:w-2/3 order-2 md:order-2">
            <div id="results" style="padding: 1.25rem;" class="bg-gray-50 rounded-lg shadow-sm">
              <!-- Initial message state (shown by default) -->
              <div id="resultsInitialMessage" class="flex flex-col items-center justify-center text-center py-12">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-brand-blue mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 class="text-xl font-medium text-gray-700 mb-2">See Your Potential Revenue Impact</h4>
                <p class="text-gray-600 max-w-md mb-4">Enter your business metrics on the left and click "Calculate Impact" to see how BluStream can help grow your revenue.</p>
              </div>
              
              <!-- Results content (hidden initially) -->
              <div id="resultsContent" class="flex flex-col gap-4 hidden">
                <!-- Annual Revenue Comparison Card -->
                <div class="p-4 bg-white rounded shadow">
                  <div class="text-sm font-medium text-gray-700 mb-2">Annual Revenue</div>
                  <div class="flex justify-between items-center">
                    <div class="flex-1">
                      <div class="text-xs text-gray-500">Current</div>
                      <div id="currentRevenue" class="text-xl font-bold text-gray-800">$0</div>
                    </div>
                    <div class="flex-none px-2 flex flex-col items-center">
                      <div id="revenueIncreasePercent" class="text-xs font-bold text-green-600 mb-1">+0%</div>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-blue" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="flex-1">
                      <div class="text-xs text-gray-500">With BluStream</div>
                      <div id="potentialRevenue" class="text-xl font-bold text-brand-blue">$0</div>
                    </div>
                  </div>
                </div>
                
                <!-- Total Incremental Revenue Card -->
                <div class="p-4 bg-white rounded shadow">
                  <div class="text-sm font-medium text-gray-700 mb-2">Total Incremental Revenue</div>
                  <div class="flex items-center">
                    <div class="flex-1">
                      <div id="incrementalRevenue" class="text-2xl font-bold text-green-600">$0</div>
                      <div class="text-xs text-gray-500 mt-1 pr-2">Potential increase in annual revenue</div>
                    </div>
                    <div class="flex-1 pl-4 border-l border-gray-200">
                      <div class="grid grid-cols-1 gap-2">
                        <div>
                          <div class="text-xs text-gray-500">From Retention</div>
                          <div id="incrementalRevenueFromRetention" class="text-base font-semibold text-green-600">$0</div>
                        </div>
                        <div>
                          <div class="text-xs text-gray-500">From Cross Sell</div>
                          <div id="incrementalRevenueFromCrossSell" class="text-base font-semibold text-green-600">$0</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Customer Lifetime Value Card -->
                <div class="p-4 bg-white rounded shadow">
                  <div class="text-sm font-medium text-gray-700 mb-2">Customer Lifetime Value (LTV)</div>
                  <div class="flex justify-between items-center">
                    <div class="flex-1">
                      <div class="text-xs text-gray-500">Current</div>
                      <div id="currentLTV" class="text-xl font-bold text-gray-800">$0</div>
                    </div>
                    <div class="flex-none px-2 flex flex-col items-center">
                      <div id="ltvIncreasePercent" class="text-xs font-bold text-green-600 mb-1">+0%</div>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-blue" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="flex-1">
                      <div class="text-xs text-gray-500">With BluStream</div>
                      <div id="potentialLTV" class="text-xl font-bold text-brand-blue">$0</div>
                    </div>
                  </div>
                </div>
                
                <!-- Impact Note -->
                <div class="text-xs text-gray-500 italic" style="margin-top: 12px;">
                  *Calculations include a 30% churn reduction and 20% increase in cross-sell revenue with BluStream. Results may vary.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  },
  
  /**
   * Set up event listeners for the calculator
   */
  setupEventListeners: function() {
    const calculateBtn = document.getElementById('calculateBtn');
    if (calculateBtn) {
      calculateBtn.addEventListener('click', () => {
        this.calculateRevenueImpact();
      });
    }
    
    // Add input validation for all numeric fields
    this.setupInputValidation('customersPerYear', 0);
    this.setupInputValidation('churnRate', 0, 100);
    this.setupInputValidation('annualSales', 0);
  },
  
  /**
   * Set up validation for a numeric input field
   * @param {string} inputId - The ID of the input element
   * @param {number} min - Minimum allowed value
   * @param {number} max - Maximum allowed value (optional)
   */
  setupInputValidation: function(inputId, min, max = null) {
    const inputElement = document.getElementById(inputId);
    if (!inputElement) return;
    
    const validateInput = () => {
      let value = parseFloat(inputElement.value);
      
      // Check if value is a number
      if (isNaN(value)) {
        value = 0;
      }
      
      // Apply constraints
      if (value < min) value = min;
      if (max !== null && value > max) value = max;
      
      // Update input if value changed
      if (value !== parseFloat(inputElement.value)) {
        inputElement.value = value;
      }
    };
    
    // Validate on change and blur events
    inputElement.addEventListener('change', validateInput);
    inputElement.addEventListener('blur', validateInput);
  },
  
  /**
   * Calculate the revenue impact based on form inputs
   */
  calculateRevenueImpact: function() {
    // Get and validate input values
    const customersInput = document.getElementById('customersPerYear');
    const churnRateInput = document.getElementById('churnRate');
    const annualSalesInput = document.getElementById('annualSales');
    
    // Trigger validation once more before calculation
    customersInput.dispatchEvent(new Event('change'));
    churnRateInput.dispatchEvent(new Event('change'));
    annualSalesInput.dispatchEvent(new Event('change'));
    
    // Get validated values
    const customersPerYear = parseFloat(customersInput.value) || 0;
    const churnRate = parseFloat(churnRateInput.value) || 0;
    const annualSales = parseFloat(annualSalesInput.value) || 0;
    
    // Switch from initial message to results content
    const initialMessage = document.getElementById('resultsInitialMessage');
    const resultsContent = document.getElementById('resultsContent');
    if (initialMessage && resultsContent) {
      // Hide the initial message
      initialMessage.classList.add('hidden');
      // Show the results content
      resultsContent.classList.remove('hidden');
      
      // Scroll to the results container if needed (especially on mobile)
      const resultsContainer = document.getElementById('results');
      if (resultsContainer && window.innerWidth < 768) {
        resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    
    // Assume BluStream reduces churn by 30%
    const churnDecrease = 0.3;
    const crossSellIncrease = 0.2;

    const currentRevenue = customersPerYear * annualSales * (1 - churnRate / 100);
    
    const potentialChurnRate = churnRate * (1 - churnDecrease);
    const newlyRetainedCustomers = customersPerYear * (churnRate - potentialChurnRate) / 100;

    const potentialCustomersPerYear = (1 - potentialChurnRate / 100) * customersPerYear;

    const potentialRevenue = (1 + crossSellIncrease) * annualSales * potentialCustomersPerYear;
    
    const incrementalRevenueFromRetention = newlyRetainedCustomers * annualSales;
    const incrementalRevenueFromCrossSell = crossSellIncrease * annualSales * potentialCustomersPerYear;

    const incrementalRevenue = potentialRevenue - currentRevenue;
    
    const currentLTV = annualSales / (churnRate / 100);
    const potentialLTV = annualSales * (1 + crossSellIncrease) / (churnRate / 100 * (1 - churnDecrease));
    
    // Calculate percentage increases
    const revenueIncreasePercent = currentRevenue ? Math.round((potentialRevenue / currentRevenue - 1) * 100) : 0;
    const ltvIncreasePercent = currentLTV ? Math.round((potentialLTV / currentLTV - 1) * 100) : 0;

    // Format currency with compact notation for large numbers
    const formatCurrency = (amount) => {
      // IRL infinite money glitch
      if (amount >= 1e15) {
        return '🤑';
      }
      
      // Use compact notation for numbers >= 10,000
      if (amount >= 10000) {
        if (amount >= 1e12) {
          return '$' + (amount / 1e12).toFixed(1) + 'T';
        } else if (amount >= 1e9) {
          return '$' + (amount / 1e9).toFixed(1) + 'B';
        } else if (amount >= 1e6) {
          return '$' + (amount / 1e6).toFixed(1) + 'M';
        } else if (amount >= 1e4) {
          return '$' + (amount / 1e3).toFixed(1) + 'K';
        }
      }
      
      // For smaller numbers, use regular formatting
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      }).format(amount);
    };
    
    // Format percentage
    const formatPercent = (percent) => {
      return `+${percent}%`;
    };
    
    // Update display
    document.getElementById('currentRevenue').textContent = formatCurrency(currentRevenue);
    document.getElementById('potentialRevenue').textContent = formatCurrency(potentialRevenue);
    document.getElementById('incrementalRevenueFromRetention').textContent = formatCurrency(incrementalRevenueFromRetention);
    document.getElementById('incrementalRevenueFromCrossSell').textContent = formatCurrency(incrementalRevenueFromCrossSell);
    document.getElementById('incrementalRevenue').textContent = formatCurrency(incrementalRevenue);
    document.getElementById('currentLTV').textContent = formatCurrency(currentLTV);
    document.getElementById('potentialLTV').textContent = formatCurrency(potentialLTV);
    
    // Add percentage increase indicators if they exist in the DOM
    const revenuePercentEl = document.getElementById('revenueIncreasePercent');
    const ltvPercentEl = document.getElementById('ltvIncreasePercent');
    
    if (revenuePercentEl) {
      revenuePercentEl.textContent = formatPercent(revenueIncreasePercent);
    }
    
    if (ltvPercentEl) {
      ltvPercentEl.textContent = formatPercent(ltvIncreasePercent);
    }
  }
};

// Make the calculator globally available
if (typeof window !== 'undefined') {
  window.revenueCalculator = revenueCalculator;
}